'use client'

import { useTheme } from '@/components/theme-provider'
import { cn } from '@/lib/utils'

// Two pre-rendered wordmarks — see /public/logo/. The asset name
// describes the LOGO'S OWN COLOUR, not the surface it sits on:
//   • velova-light.png — light-coloured (white) wordmark, designed to
//                         appear on dark backgrounds (dark theme, landing).
//   • velova-dark.png  — dark-coloured (black) wordmark, designed to
//                         appear on light backgrounds (light theme, onboarding).
const LOGO_LIGHT_SRC = '/logo/velova-light.png'
const LOGO_DARK_SRC  = '/logo/velova-dark.png'
// Source PNGs are 1983×793. Locking the ratio prevents distortion when
// callers pass only `width` — height auto-derives via `h-auto`.
const LOGO_ASPECT_RATIO = 1983 / 793

type LogoVariant = 'auto' | 'light' | 'dark'
// Earlier versions of this component used `variant` for layout; the two
// values appear at every existing call site. Keep them accepted (treated
// as 'auto') so the new theme-aware `variant` prop is purely additive.
type LegacyLayout = 'inline' | 'stacked'

interface VelovaLogoProps {
  /**
   * Theme override for the rendered wordmark.
   *  - 'auto'  → swap with the user's theme (default).
   *  - 'light' → always render the light-coloured (white) wordmark — use
   *              on dark backgrounds (landing hero, marketing pages).
   *  - 'dark'  → always render the dark-coloured (black) wordmark — use
   *              on persistent light backgrounds (onboarding flow).
   * Legacy values 'inline' / 'stacked' are accepted for back-compat and
   * resolve to 'auto'.
   */
  variant?: LogoVariant | LegacyLayout
  className?: string
  /** @deprecated use `variant="light"`. Retained so existing call sites
   * (beacon-shell, etc.) continue to compile and behave identically. */
  forceDark?: boolean
  /** Pixel width — height auto-derives from the source aspect ratio. */
  width?: number
}

export function VelovaLogo({
  variant = 'auto',
  className = '',
  forceDark = false,
  width,
}: VelovaLogoProps) {
  const { theme } = useTheme()

  // Resolve the legacy `variant="inline" | "stacked"` calls down to a
  // theme-aware variant so this component has a single decision path.
  const isLegacyLayout = variant === 'inline' || variant === 'stacked'
  const themeVariant: LogoVariant = forceDark
    ? 'light'
    : isLegacyLayout
    ? 'auto'
    : variant

  // The light-coloured wordmark wins on (a) explicit 'light' override or
  // (b) auto + dark theme. Otherwise show the dark-coloured wordmark.
  const useLightLogo =
    themeVariant === 'light' ||
    (themeVariant === 'auto' && theme === 'dark')

  const logoSrc = useLightLogo ? LOGO_LIGHT_SRC : LOGO_DARK_SRC
  // Preserve the previous defaults: stacked = 180, inline = 120. New
  // variant strings inherit the inline default.
  const w = width ?? (variant === 'stacked' ? 180 : 120)

  return (
    <div className={cn('flex items-center justify-center', className)}>
      <img
        src={logoSrc}
        alt="Velova"
        width={w}
        className="block h-auto object-contain"
        style={{ width: w, aspectRatio: LOGO_ASPECT_RATIO }}
      />
    </div>
  )
}

/** Icon-only variant — uses the same wordmark scaled down inside an
 * 8×8 square crop. Theme-aware via the same `forceDark` alias.
 * `variant` forwards straight through to VelovaLogo, so an icon sitting on
 * a surface that is pinned to one theme (e.g. the permanently-white
 * AppShell sidebar) can pin the wordmark colour to match. Omitted →
 * VelovaLogo's own 'auto' default, so existing call sites are unchanged. */
export function VelovaIcon({
  className = '',
  forceDark = false,
  variant,
}: {
  className?: string
  forceDark?: boolean
  variant?: LogoVariant | LegacyLayout
}) {
  return (
    <VelovaLogo
      className={cn('w-8 h-8 overflow-hidden', className)}
      width={80}
      forceDark={forceDark}
      {...(variant ? { variant } : {})}
    />
  )
}
